import { useEffect, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { PLANNING_CATEGORY_ARTWORK } from "../lib/planning/categoryArtwork";
import { useRouter } from "next/router";
import {
  ArrowRight,
  ExternalLink,
  CheckCircle,
  Search,
} from "lucide-react";
import Meta from "../components/Meta";
import CouncilDirectory from "../components/planning/CouncilDirectory";
import styles from "../styles/home-discovery.module.css";
import { trackMatomoEvent } from "../lib/matomoTracking";
import { trackHomepageSearchSubmission } from "../lib/homepageSearchAnalytics";
import {
  PUBLIC_PLANNING_COUNCILS,
  PLANNING_COVERAGE_SUMMARY,
} from "../lib/planningSeo";
import { buildPlanningApplicationPath } from "../lib/planningApplicationUrl";
import { COUNCILS, getCouncilQualifiedApplicationReference } from "../lib/councils";
import { requestPlanningUpdatesResultsScroll } from "../lib/planning/homepageSearchScroll";
import {
  SHORT_LETS_CATEGORY_ID,
  isShortLetsSearchQuery,
} from "../lib/planning/shortLetsSearch";
import {
  CITY_SCOPE_BRAND_KEYWORDS,
  CITY_SCOPE_BRAND_NAME,
  CITY_SCOPE_BRAND_SEARCH_VARIANTS,
} from "../lib/brandSeo";

const structuredData = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "@id": "https://www.city-scope.co.uk/#homepage",
  name: CITY_SCOPE_BRAND_NAME,
  alternateName: CITY_SCOPE_BRAND_SEARCH_VARIANTS,
  url: "https://www.city-scope.co.uk",
  description: `Search Scottish planning applications by address, postcode or reference. ${PLANNING_COVERAGE_SUMMARY}`,
  spatialCoverage: PUBLIC_PLANNING_COUNCILS.map((council) => ({ "@type": "AdministrativeArea", name: council.officialName })),
  keywords: `${CITY_SCOPE_BRAND_KEYWORDS}, Scotland planning applications, Edinburgh planning applications, Midlothian planning applications, East Lothian planning applications, West Lothian planning applications, Falkirk planning applications, Clackmannanshire planning applications, planning updates, planning map, weekly planning reports`,
  isPartOf: { "@id": "https://www.city-scope.co.uk/#website" },
  publisher: { "@id": "https://www.city-scope.co.uk/#organization" },
  about: [
    "Local planning applications",
    "Planning application search",
    "City Scope planning applications",
    "CityScope planning applications",
    "city-scope planning applications",
    "Planning map",
    "Weekly planning updates",
    "Edinburgh planning applications",
    "Midlothian planning applications",
    "East Lothian planning applications",
    "West Lothian planning applications",
    "Falkirk planning applications",
    "Clackmannanshire planning applications",
    "Scottish council planning applications",
  ],
};

const trackClick = (pathway) => {
  if (typeof window !== "undefined" && window.gtag) {
    window.gtag("event", "homepage_pathway_click", { pathway });
  }
  trackMatomoEvent({
    category: "homepage",
    action: "pathway_click",
    name: pathway,
  });
};

const PUBLIC_COUNCIL_COUNT = PUBLIC_PLANNING_COUNCILS.length;
const DEFAULT_PUBLIC_APPLICATION_COUNT = null;
const formatApplicationCount = (value) => value == null ? '—' : Number(value).toLocaleString("en-GB");


const cleanText = (value) => (typeof value === "string" ? value.replace(/\s+/g, " ").trim() : "");

const truncateText = (value, maxLength = 112) => {
  const text = cleanText(value);
  if (text.length <= maxLength) return text;

  const fallbackCut = maxLength - 3;
  const wordCut = text.lastIndexOf(" ", fallbackCut);
  const cutAt = wordCut > Math.floor(maxLength * 0.55) ? wordCut : fallbackCut;
  return `${text.slice(0, cutAt).trim()}...`;
};

const getApplicationReference = (application) =>
  cleanText(
    application?.reference_no ||
      application?.ref ||
      application?.reference ||
      application?.application_ref ||
      application?.id
  );

const formatRegisterPreviewRecord = (application) => {
  const ref = getApplicationReference(application);
  const council = cleanText(
    application?.source_council ||
      application?.council_slug ||
      application?.council ||
      application?.sourceCouncil
  );
  const title =
    truncateText(application?.ai_summary || application?.description || application?.address) ||
    "Planning application";
  const scaleRating = Number(application?.scale_rating);
  const category = cleanText(application?.category).replace(/_/g, " ");
  const status = cleanText(application?.status || application?.decision_status || application?.application_status);
  const metaParts = [];
  const councilConfig = COUNCILS[council];
  if (councilConfig) metaParts.push(councilConfig.displayName);

  if (Number.isFinite(scaleRating)) {
    metaParts.push(`Scale ${scaleRating}`);
  }
  if (category) {
    metaParts.push(category);
  } else if (status) {
    metaParts.push(status);
  }

  return {
    ref,
    displayRef: getCouncilQualifiedApplicationReference(application),
    href: buildPlanningApplicationPath(ref, council),
    title,
    meta: metaParts.join(" / ") || "Current application",
  };
};

function HeroSearch() {
  const router = useRouter();
  const [query, setQuery] = useState("");

  const submitSearch = (value = query, placement = "search_input") => {
    const cleaned = value.trim();
    if (!cleaned) {
      router.push("/planning-updates");
      return;
    }
    trackHomepageSearchSubmission(cleaned, placement);
    requestPlanningUpdatesResultsScroll();
    const searchQuery = { q: cleaned };
    if (isShortLetsSearchQuery(cleaned)) {
      searchQuery.category = SHORT_LETS_CATEGORY_ID;
      searchQuery.categories = SHORT_LETS_CATEGORY_ID;
    }
    router.push({
      pathname: "/planning-updates",
      query: searchQuery,
    });
  };

  return (
    <div className="mt-10 w-full max-w-2xl" role="search" aria-label="Search planning applications">
      <form
        className="pr-home-search flex items-stretch border border-[var(--c-border)] bg-[var(--c-surface)] transition-colors focus-within:border-[var(--c-text-primary)]"
        data-analytics-form="homepage_search"
        onSubmit={(event) => {
          event.preventDefault();
          submitSearch();
        }}
      >
        <Search
          className="ml-4 h-5 w-5 flex-shrink-0 self-center text-[var(--c-accent)] sm:ml-5"
          strokeWidth={1.6}
          aria-hidden
        />
        <input
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search an address, postcode or reference..."
          aria-label="Search an address, postcode or planning reference"
          className="h-14 min-w-0 flex-1 bg-transparent px-3 font-['Inter'] text-base text-[var(--c-text-primary)] outline-none placeholder:text-[var(--c-text-tertiary)] sm:h-16 sm:px-4 sm:text-lg"
          data-global-search-input="true"
          autoComplete="off"
          spellCheck={false}
        />
        <button
          type="submit"
          className="m-1.5 hidden items-center gap-2 bg-[var(--c-text-primary)] px-6 font-['Inter'] text-sm font-semibold text-[var(--c-bg)] transition-opacity hover:opacity-85 sm:inline-flex"
        >
          Search
          <ArrowRight className="h-4 w-4" strokeWidth={1.6} aria-hidden />
        </button>
        <button
          type="submit"
          aria-label="Search"
          className="m-1.5 inline-flex w-12 items-center justify-center bg-[var(--c-text-primary)] text-[var(--c-bg)] transition-opacity hover:opacity-85 sm:hidden"
        >
          <ArrowRight className="h-5 w-5" strokeWidth={1.6} aria-hidden />
        </button>
      </form>
      <div className={styles.suggestions}>
        <span>Try</span>
        {['extensions', 'short term let', 'listed building'].map((item) => (
          <button key={item} type="button" onClick={() => submitSearch(item, 'quick_search')}>{item}</button>
        ))}
      </div>
      <div className="mt-3 flex justify-center">
        <Link
          href="/planning-updates"
          prefetch={false}
          onClick={() => trackClick("hero_browse_planning_updates")}
          className="pr-textlink text-sm"
        >
          Browse all applications
          <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.6} aria-hidden />
        </Link>
      </div>

    </div>
  );
}

function RegisterPreview() {
  const [previewState, setPreviewState] = useState({
    records: [],
    status: "loading",
  });

  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    const loadPreviewRecords = async () => {
      try {
        const response = await fetch("/api/planning-feed/top", {
          headers: { Accept: "application/json" },
          signal: controller.signal,
          cache: "no-store",
        });

        if (!response.ok) {
          throw new Error(`Planning feed returned ${response.status}`);
        }

        const payload = await response.json();
        if (!Array.isArray(payload.applications) || !payload.month?.label) throw new Error("Invalid top applications response");
        const records = payload.applications.map(formatRegisterPreviewRecord);

        if (isMounted) {
          setPreviewState({
            records,
            status: records.length > 0 ? "ready" : "empty",
            month: payload.month.label,
          });
        }
      } catch (error) {
        if (error?.name === "AbortError" || !isMounted) return;
        setPreviewState({ records: [], status: "error" });
      }
    };

    loadPreviewRecords();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, []);

  const statusLabel = {
    ready: previewState.month,
    loading: "Loading",
    empty: "No records",
    error: "Unavailable",
  }[previewState.status] || "Loading";
  return (
    <aside className="pr-panel overflow-hidden" aria-label="Top planning applications this month">
      <div className="border-b border-[var(--c-border)] p-4 sm:p-5">
        <div className="flex items-center justify-between gap-4">
          <div className="pr-section-label">Highest scale this month</div>
          <span className="pr-data text-[10px] uppercase tracking-[0.14em] text-[var(--c-text-tertiary)]">
            {statusLabel}
          </span>
        </div>

      </div>

      <div className="grid gap-0 divide-y divide-[var(--c-border)]">
        {previewState.records.length > 0 ? (
          previewState.records.map((record) => (
            <Link
              key={`${record.ref}-${record.href}`}
              href={record.href}
              prefetch={false}
              aria-label={`Open planning application ${record.displayRef}`}
              className="group grid gap-2 p-4 no-underline transition-colors hover:bg-[var(--c-bg-secondary)] focus:outline-none focus-visible:bg-[var(--c-bg-secondary)] sm:grid-cols-[8rem_minmax(0,1fr)] sm:p-5"
            >
              <span className="pr-data font-medium text-[var(--c-text-primary)]">{record.displayRef}</span>
              <div className="min-w-0">
                <p className="font-['Newsreader'] text-xl leading-tight text-[var(--c-text-primary)] transition-colors group-hover:text-[var(--c-accent)] group-focus-visible:text-[var(--c-accent)]">
                  {record.title}
                </p>
                <p className="pr-data mt-2 uppercase tracking-[0.08em]">{record.meta}</p>
              </div>
            </Link>
          ))
        ) : (
          <div className="grid gap-2 p-4 sm:grid-cols-[8rem_minmax(0,1fr)] sm:p-5">
            <span className="pr-data font-medium text-[var(--c-text-primary)]">
              {previewState.status === "loading" ? "Loading" : "Feed"}
            </span>
            <div className="min-w-0">
              <p className="font-['Newsreader'] text-xl leading-tight text-[var(--c-text-primary)]">
                {previewState.status === "loading"
                  ? "Loading top applications"
                  : previewState.status === "empty" ? "No scored applications received this month yet" : "Top applications temporarily unavailable"}
              </p>
              <p className="pr-data mt-2 uppercase tracking-[0.08em]">
                {previewState.status === "loading" ? "Checking this month" : "Try Planning Updates"}
              </p>
            </div>
          </div>
        )}
      </div>
    </aside>
  );
}

function WeeklyReportPanel() {
  return (
    <section className={styles.weekly}>
      <div>
        <h2 className="pr-heading">The week in planning.</h2>
        <p>A Monday digest of notable applications, delivered to your inbox.</p>
      </div>
      <Link href="/reports/signup" onClick={() => trackClick("weekly_reports_section")} className="pr-textlink">Get the weekly report <ArrowRight size={16} aria-hidden /></Link>
    </section>
  );
}

function Footer() {
  return (
    <footer className="border-t border-[var(--c-border)] py-10">
      <div className="mx-auto flex max-w-7xl flex-col gap-5 px-4 sm:px-6 lg:flex-row lg:items-center lg:justify-between lg:px-8">
        <p className="text-sm text-[var(--c-text-secondary)]">
          City Scope makes Scottish planning applications easier to find and understand.
        </p>
        <nav aria-label="Footer navigation" className="flex flex-wrap gap-4">
          {[
            ["Planning Updates", "/planning-updates"],
            ["Planning Map", "/planning-map"],
            ["Planning Archive", "/planning-archive"],
            ["Weekly Reports", "/reports/signup"],
            ["About", "/about"],
          ].map(([label, href]) => (
            <Link key={href} href={href} className="pr-textlink">
              {label}
            </Link>
          ))}
        </nav>
      </div>
    </footer>
  );
}

export default function HomePage() {
  const [publicApplicationCount, setPublicApplicationCount] = useState(DEFAULT_PUBLIC_APPLICATION_COUNT);

  useEffect(() => {
    const controller = new AbortController();
    fetch("/api/planning-feed/counts", {
      headers: { Accept: "application/json" },
      signal: controller.signal,
      cache: "no-store",
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((payload) => {
        const counts = payload?.counts;
        if (payload?.source !== "backend" || !counts || typeof counts !== "object") return;
        if (!PUBLIC_PLANNING_COUNCILS.every((council) =>
          typeof counts[council.slug] === "number" && Number.isFinite(counts[council.slug]) && counts[council.slug] >= 0
        )) return;
        const total = Object.values(COUNCILS)
          .filter((council) => council.publicEnabled)
          .reduce((sum, council) => sum + (Number(counts[council.slug]) || 0), 0);
        setPublicApplicationCount(total);
      })
      .catch((error) => {
        if (error?.name !== "AbortError") console.warn("Homepage planning count refresh failed", error);
      });
    return () => controller.abort();
  }, []);

  return (
    <>
      <Meta
        title="Scottish Planning Applications Search | City Scope"
        description={`Search Scottish planning applications by address, postcode or reference. Explore council updates and the planning map. ${PLANNING_COVERAGE_SUMMARY}`}
        keywords={`${CITY_SCOPE_BRAND_KEYWORDS}, planning applications, local planning applications, Edinburgh planning, Midlothian planning, East Lothian planning, West Lothian planning, Falkirk planning, Clackmannanshire planning, planning map, council planning`}
        canonical="/"
        structuredData={structuredData}
      />

      <div>
        <main className="planning-register min-h-screen bg-[var(--c-bg)] text-[var(--c-text-primary)]">
          <section className={`pr-home-hero ${styles.hero}`}>
            <div className={styles.heroInner}>
              <div className={styles.heroCopy}>
                <h1 className={styles.headline}>Scotland&apos;s<br /><em>planning applications.</em></h1>
                <div className={styles.artwork} aria-label="Planning illustrations">
                  {[
                    [PLANNING_CATEGORY_ARTWORK.new_build_major, 'New buildings'],
                    [PLANNING_CATEGORY_ARTWORK.extensions_enlargements, 'Home extensions'],
                    [PLANNING_CATEGORY_ARTWORK.trees_ecology, 'Trees and green spaces'],
                  ].map(([src, alt]) => (
                    <div key={src} className="pr-home-category-tile"><Image src={src} alt={alt} width={104} height={104} unoptimized /></div>
                  ))}
                </div>
                <p className={styles.intro}>Find out what&apos;s planned near you. Search by address, postcode or reference.</p>
                <HeroSearch />
                <div className={styles.coverageLine}>
                  <span>{PUBLIC_COUNCIL_COUNT} Scottish council areas</span>
                  <span>{publicApplicationCount == null ? 'Search public planning records' : `${formatApplicationCount(publicApplicationCount)} applications on record`}</span>

                </div>
              </div>

            </div>
          </section>
          <section id="councils" className={styles.councils}>
            <CouncilDirectory />
          </section>

          {/* ---- Current updates preview ---- */}
          <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
            <div className="mb-7 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <h2 className="pr-heading mt-4">Top Applications</h2>
                <p className="mt-2 text-sm text-[var(--c-text-secondary)]">Highest published scale scores among applications received this month, across all live councils.</p>
              </div>
              <Link href="/planning-map" className="pr-textlink">Explore the map <ArrowRight size={16} aria-hidden /></Link>
            </div>
            <RegisterPreview />
          </section>

          {/* ---- Weekly reports ---- */}
          <section className="mx-auto max-w-7xl px-4 pb-16 sm:px-6 lg:px-8">
            <WeeklyReportPanel />
          </section>

          {/* ---- Press ---- */}
          <section className="border-y border-[var(--c-border)] bg-[var(--c-surface)]">
            <div className="mx-auto grid max-w-7xl gap-8 px-4 py-14 sm:px-6 lg:grid-cols-[minmax(0,1fr)_minmax(300px,420px)] lg:px-8">
              <div>
                <p className="pr-section-label">Featured in</p>
                <blockquote className="mt-5 max-w-3xl font-['Newsreader'] text-3xl leading-tight text-[var(--c-text-primary)]">
                  &ldquo;City Scope makes local planning easier to access and understand.&rdquo;
                </blockquote>
                <p className="mt-4 max-w-2xl text-sm leading-6 text-[var(--c-text-secondary)]">
                  Built for residents, civic groups, journalists and planning professionals who need a clearer route through public planning data.
                </p>
              </div>
              <div className="grid gap-3">
                {[
                  {
                    outlet: "Deadline News",
                    title: "Edinburgh local creates handy tool to view planning applications across city",
                    url: "https://www.deadlinenews.co.uk/2025/06/06/edinburgh-local-creates-handy-tool-to-view-planning-applications-across-city/",
                  },
                  {
                    outlet: "Edinburgh Minute",
                    title: "User-friendly way to find out about planning applications anywhere in Edinburgh",
                    url: "https://edinburghminute.substack.com/p/edinburgh-minute-weekend-edition-6-june-2025",
                  },
                ].map((item) => (
                  <a key={item.url} href={item.url} target="_blank" rel="noopener noreferrer" className="pr-panel group block p-4 hover:border-[var(--c-text-tertiary)]">
                    <span className="pr-data uppercase tracking-[0.14em] text-[var(--c-accent)]">{item.outlet}</span>
                    <span className="mt-2 flex items-start justify-between gap-4 text-sm leading-6 text-[var(--c-text-primary)]">
                      {item.title}
                      <ExternalLink className="mt-1 h-4 w-4 flex-shrink-0 text-[var(--c-text-tertiary)] group-hover:text-[var(--c-accent)]" strokeWidth={1.5} aria-hidden />
                    </span>
                  </a>
                ))}
              </div>
            </div>
          </section>

          {/* ---- Final CTA ---- */}
          <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
            <div className="pr-panel grid gap-6 p-6 sm:p-8 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-center">
              <div>
                <p className="pr-section-label">Stay informed</p>
                <h2 className="pr-heading mt-4">Start with live updates, then subscribe when it is useful.</h2>
                <p className="mt-4 max-w-2xl text-sm leading-6 text-[var(--c-text-secondary)]">
                  Browse first. When you want the weekly planning changes delivered to you, sign up for the Monday report.
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                <Link href="/planning-updates" onClick={() => trackClick("final_cta_browse")} className="pr-button pr-button--ghost">
                  Browse applications
                  <Search className="h-4 w-4" strokeWidth={1.5} aria-hidden />
                </Link>
                <Link href="/reports/signup" onClick={() => trackClick("final_cta_weekly")} className="pr-button">
                  Get Weekly Reports
                  <CheckCircle className="h-4 w-4" strokeWidth={1.5} aria-hidden />
                </Link>
              </div>
            </div>
          </section>

          <Footer />
        </main>
      </div>
    </>
  );
}
